import { useState, useEffect, useMemo } from 'react';
import axios from '../../axios/axiosconf';
import Header from '../general/Header';
import Aside from '../general/Aside';
import Footer from '../general/Footer';
import Unauthorized from '../general/Unauthorized';
import Loading from '../general/Loading';
import { Link } from 'react-router-dom';

const getFechaActual = () => {
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
};

const getInicioMes = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
};

function tienePermisoReportes(userdata) {
    if (userdata.permissions && Array.isArray(userdata.permissions)) {
        return userdata.permissions.some((p) => p.slug === 'verReportes');
    }
    return Boolean(userdata.up && userdata.up.includes('verReportes'));
}

function ReporteTratamientosFacturadosBase({ porPaciente = false }) {
    const [loggedIn, setLoggedIn] = useState(false);
    const [hasPermission, setHasPermission] = useState(null);
    const [client, setClient] = useState({});
    const [filas, setFilas] = useState([]);
    const [totales, setTotales] = useState({ registros: 0, cantidad_total: 0, valor_total: 0 });
    const [usuarios, setUsuarios] = useState([]);
    const [servicios, setServicios] = useState([]);
    const [fechaInicio, setFechaInicio] = useState('');
    const [fechaFin, setFechaFin] = useState('');
    const [usuarioFiltro, setUsuarioFiltro] = useState('');
    const [servicioFiltro, setServicioFiltro] = useState('');
    const [clasificacionFiltro, setClasificacionFiltro] = useState('');
    const [buscarServicio, setBuscarServicio] = useState('');
    const [mostrarOpcionesServicio, setMostrarOpcionesServicio] = useState(false);
    const [busquedaPaciente, setBusquedaPaciente] = useState('');
    const [loading, setLoading] = useState(false);
    const [buscado, setBuscado] = useState(false);

    const endpointBase = porPaciente
        ? '/reportes/tratamientos-facturados-por-paciente'
        : '/reportes/tratamientos-facturados';

    useEffect(() => {
        if (!localStorage.getItem('userdata')) {
            setHasPermission(false);
            setLoggedIn(true);
            return;
        }

        const userdata = JSON.parse(localStorage.getItem('userdata'));
        const ok = tienePermisoReportes(userdata);
        setHasPermission(ok);
        if (!ok) {
            setLoggedIn(true);
            return;
        }

        setFechaInicio(getInicioMes());
        setFechaFin(getFechaActual());

        axios.get('/get-client?uid=' + userdata.user_id)
            .then((response) => {
                if (response.status === 200) {
                    setClient(response.data);
                }
                setLoggedIn(true);
            })
            .catch(() => setLoggedIn(true));

        axios.get('/users-by-client?uid=' + userdata.user_id)
            .then((response) => {
                if (response.status === 200) {
                    setUsuarios(response.data || []);
                }
            })
            .catch(() => setUsuarios([]));

        axios.get('/lista-precios')
            .then((response) => {
                const data = Array.isArray(response.data) ? response.data : (response.data?.data || []);
                setServicios(data);
            })
            .catch(() => setServicios([]));
    }, []);

    const clasificaciones = useMemo(() => {
        const unicas = new Set(
            servicios.map((s) => (s.clasificacion || '').trim()).filter(Boolean)
        );
        return Array.from(unicas).sort((a, b) => a.localeCompare(b, 'es'));
    }, [servicios]);

    const serviciosFiltrados = useMemo(() => {
        let list = servicios;
        if (clasificacionFiltro) {
            list = list.filter((s) => s.clasificacion === clasificacionFiltro);
        }
        if (buscarServicio.trim()) {
            const term = buscarServicio.toLowerCase();
            list = list.filter(
                (s) =>
                    (s.nombre || '').toLowerCase().includes(term) ||
                    (s.codigo || '').toLowerCase().includes(term)
            );
        }
        return list;
    }, [servicios, clasificacionFiltro, buscarServicio]);

    const etiquetaServicio = (servicio) => {
        const codigo = (servicio.codigo || '').trim();
        const nombre = (servicio.nombre || '').trim();
        if (codigo && nombre) return `${codigo} - ${nombre}`;
        return codigo || nombre || `Servicio #${servicio.id}`;
    };

    const limpiarServicioFiltro = () => {
        setServicioFiltro('');
        setBuscarServicio('');
        setMostrarOpcionesServicio(false);
    };

    const seleccionarServicioFiltro = (servicio) => {
        setServicioFiltro(String(servicio.id));
        setBuscarServicio(etiquetaServicio(servicio));
        setMostrarOpcionesServicio(false);
    };

    const handleCambioClasificacion = (clas) => {
        setClasificacionFiltro(clas);
        if (servicioFiltro) {
            const seleccionado = servicios.find((s) => String(s.id) === String(servicioFiltro));
            if (seleccionado && clas && seleccionado.clasificacion !== clas) {
                limpiarServicioFiltro();
            }
        }
    };

    const handleCambioBuscarServicio = (valor) => {
        setBuscarServicio(valor);
        setServicioFiltro('');
        setMostrarOpcionesServicio(true);
    };

    const buildParams = () => {
        const params = {
            fecha_inicio: fechaInicio,
            fecha_fin: fechaFin,
        };
        if (usuarioFiltro) params.usuario_id = usuarioFiltro;
        if (servicioFiltro) params.servicio_id = servicioFiltro;
        if (porPaciente && busquedaPaciente.trim()) {
            params.busqueda_paciente = busquedaPaciente.trim();
        }
        return params;
    };

    const cargarReporte = () => {
        if (!fechaInicio || !fechaFin) return;
        setLoading(true);
        axios.get(endpointBase, { params: buildParams() })
            .then((response) => {
                setFilas(response.data?.filas || []);
                setTotales(response.data?.totales || { registros: 0, cantidad_total: 0, valor_total: 0 });
                setBuscado(true);
            })
            .catch((err) => {
                console.error('Error cargando reporte:', err);
                alert(err.response?.data?.message || 'No se pudo cargar el reporte');
            })
            .finally(() => setLoading(false));
    };

    const handleBuscar = (e) => {
        e.preventDefault();
        cargarReporte();
    };

    const handleLimpiar = () => {
        setFechaInicio(getInicioMes());
        setFechaFin(getFechaActual());
        setUsuarioFiltro('');
        setServicioFiltro('');
        setClasificacionFiltro('');
        setBuscarServicio('');
        setMostrarOpcionesServicio(false);
        setBusquedaPaciente('');
        setFilas([]);
        setTotales({ registros: 0, cantidad_total: 0, valor_total: 0 });
        setBuscado(false);
    };

    const handleExportarExcel = () => {
        if (!fechaInicio || !fechaFin) return;
        setLoading(true);
        const queryString = new URLSearchParams(buildParams()).toString();
        const url = `${endpointBase}/exportar-excel?${queryString}`;

        axios.get(url, { responseType: 'blob' })
            .then((response) => {
                const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement('a');
                link.href = blobUrl;
                link.setAttribute('download', `${porPaciente ? 'tratamientos_por_paciente' : 'tratamientos_facturados'}_${Date.now()}.xlsx`);
                document.body.appendChild(link);
                link.click();
                link.remove();
            })
            .catch(() => alert('Error al exportar el reporte a Excel'))
            .finally(() => setLoading(false));
    };

    const formatearMoneda = (valor) =>
        '$' + Number(valor || 0).toLocaleString('es-CO', { minimumFractionDigits: 0, maximumFractionDigits: 0 });

    const titulo = porPaciente ? 'Tratamientos facturados por paciente' : 'Tratamientos facturados';
    const subtitulo = porPaciente
        ? 'Desglose por paciente e ítem de lista de precios'
        : 'Totales por ítem de lista de precios';

    if (!loggedIn) return <Loading />;
    if (hasPermission === false) return <Unauthorized />;

    return (
        <div className="wrapper hold-transition" style={{ fontSize: '13px' }}>
            <Header client={client} />
            <Aside client={client} activelink="reports" />

            <div className="content-wrapper">
                <div className="content-header">
                    <div className="container-fluid">
                        <div className="row mb-2">
                            <div className="col-sm-12">
                                <ol className="breadcrumb float-sm-left mb-0">
                                    <li className="breadcrumb-item"><Link to="/home">Home</Link></li>
                                    <li className="breadcrumb-item"><Link to="/estadisticas">Reportes</Link></li>
                                    <li className="breadcrumb-item"><Link to="/estadisticas-contables">Contables</Link></li>
                                    <li className="breadcrumb-item active">{titulo}</li>
                                </ol>
                            </div>
                        </div>
                        <div className="row mb-2 align-items-end">
                            <div className="col-md-8">
                                <div className="dash-wrap">
                                    <h1 className="dash-title">{titulo}</h1>
                                    <p className="dash-subtitle mb-0">{subtitulo}</p>
                                </div>
                            </div>
                            <div className="col-md-4 mt-2 mt-md-0 text-md-right">
                                <Link to="/estadisticas-contables" className="btn btn-outline-secondary btn-sm" style={{ borderRadius: '10px', fontWeight: 600 }}>
                                    <i className="fas fa-arrow-left mr-1" /> Volver
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>

                <section className="content">
                    <div className="container-fluid">
                        <div className="dash-card mb-3">
                            <div className="dash-card-body">
                                <form onSubmit={handleBuscar}>
                                    <div className="row">
                                        <div className={`${porPaciente ? 'col-md-3' : 'col-md-4'} form-group`}>
                                            <label htmlFor="fecha_inicio">Desde</label>
                                            <input type="date" id="fecha_inicio" className="form-control form-control-sm" value={fechaInicio} onChange={(e) => setFechaInicio(e.target.value)} required />
                                        </div>
                                        <div className={`${porPaciente ? 'col-md-3' : 'col-md-4'} form-group`}>
                                            <label htmlFor="fecha_fin">Hasta</label>
                                            <input type="date" id="fecha_fin" className="form-control form-control-sm" value={fechaFin} onChange={(e) => setFechaFin(e.target.value)} required />
                                        </div>
                                        <div className={`${porPaciente ? 'col-md-3' : 'col-md-4'} form-group`}>
                                            <label htmlFor="usuario_filtro">Profesional</label>
                                            <select id="usuario_filtro" className="form-control form-control-sm" value={usuarioFiltro} onChange={(e) => setUsuarioFiltro(e.target.value)}>
                                                <option value="">Todos</option>
                                                {usuarios.map((u) => (
                                                    <option key={u.id} value={u.id}>{u.name} {u.lastname}</option>
                                                ))}
                                            </select>
                                        </div>
                                        {porPaciente && (
                                            <div className="col-md-3 form-group">
                                                <label htmlFor="busqueda_paciente">Paciente</label>
                                                <input type="text" id="busqueda_paciente" className="form-control form-control-sm" placeholder="Nombre o cédula" value={busquedaPaciente} onChange={(e) => setBusquedaPaciente(e.target.value)} />
                                            </div>
                                        )}
                                    </div>
                                    <div className="row">
                                        <div className="col-md-4 form-group">
                                            <label htmlFor="servicio_filtro">Servicio / tratamiento</label>
                                            <div className="report-servicio-autocomplete">
                                                <input
                                                    type="text"
                                                    id="servicio_filtro"
                                                    className="form-control form-control-sm"
                                                    placeholder="Buscar por código o nombre…"
                                                    value={buscarServicio}
                                                    autoComplete="off"
                                                    onFocus={() => setMostrarOpcionesServicio(true)}
                                                    onBlur={() => setTimeout(() => setMostrarOpcionesServicio(false), 150)}
                                                    onChange={(e) => handleCambioBuscarServicio(e.target.value)}
                                                />
                                                {buscarServicio && (
                                                    <button
                                                        type="button"
                                                        className="report-servicio-autocomplete-clear"
                                                        title="Quitar filtro de servicio"
                                                        onMouseDown={(e) => e.preventDefault()}
                                                        onClick={limpiarServicioFiltro}
                                                    >
                                                        ×
                                                    </button>
                                                )}
                                                {mostrarOpcionesServicio && (
                                                    <div className="report-servicio-autocomplete-dropdown">
                                                        {serviciosFiltrados.length === 0 ? (
                                                            <div className="report-servicio-autocomplete-empty">
                                                                No hay servicios para tu búsqueda.
                                                            </div>
                                                        ) : (
                                                            <>
                                                                {serviciosFiltrados.slice(0, 30).map((servicio) => (
                                                                    <button
                                                                        key={servicio.id}
                                                                        type="button"
                                                                        className="report-servicio-autocomplete-option"
                                                                        onMouseDown={() => seleccionarServicioFiltro(servicio)}
                                                                    >
                                                                        <span className="report-servicio-autocomplete-code">{servicio.codigo || '—'}</span>
                                                                        <span className="report-servicio-autocomplete-name"> - {servicio.nombre || ''}</span>
                                                                    </button>
                                                                ))}
                                                                {serviciosFiltrados.length > 30 && (
                                                                    <div className="report-servicio-autocomplete-more">
                                                                        Mostrando 30 de {serviciosFiltrados.length}. Escriba más para refinar.
                                                                    </div>
                                                                )}
                                                            </>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        <div className="col-md-4 form-group">
                                            <label>Clasificación</label>
                                            <div className="report-chip-row">
                                                <button
                                                    type="button"
                                                    className={`report-chip-filter${clasificacionFiltro === '' ? ' active' : ''}`}
                                                    onClick={() => handleCambioClasificacion('')}
                                                >
                                                    Todas
                                                </button>
                                                {clasificaciones.map((clas) => (
                                                    <button
                                                        key={clas}
                                                        type="button"
                                                        className={`report-chip-filter${clasificacionFiltro === clas ? ' active' : ''}`}
                                                        onClick={() => handleCambioClasificacion(clas)}
                                                    >
                                                        {clas}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="row">
                                        <div className="col-12">
                                            <div className="d-flex flex-wrap justify-content-end report-tratamientos-actions">
                                                <button type="button" className="btn btn-outline-secondary btn-sm" onClick={handleLimpiar} disabled={loading}>
                                                    Limpiar
                                                </button>
                                                <button type="button" className="btn btn-success btn-sm" onClick={handleExportarExcel} disabled={loading || !buscado || filas.length === 0}>
                                                    <i className="fas fa-file-excel mr-1" /> Excel
                                                </button>
                                                <button type="submit" className="btn btn-primary btn-sm" disabled={loading}>
                                                    {loading ? 'Consultando…' : 'Consultar'}
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </form>
                            </div>
                        </div>

                        <div className="card report-fin-card report-fin-card--datatable">
                            <div className="card-header">
                                <h3 className="card-title mb-0">
                                    Resultados
                                    {buscado && filas.length > 0 ? ` (${filas.length})` : ''}
                                </h3>
                            </div>
                            <div className="card-body p-0">
                                <div className={`report-fin-table-wrap report-tratamientos-table-wrap${porPaciente ? '' : ' report-tratamientos-table-wrap--fit'}`}>
                                    <table className="table table-hover mb-0 report-fin-table report-tratamientos-table">
                                        <thead>
                                            <tr>
                                                {porPaciente && (
                                                    <>
                                                        <th className="text-center">Documento</th>
                                                        <th className="text-center">Paciente</th>
                                                    </>
                                                )}
                                                <th className="text-center">Código</th>
                                                <th className="text-center">Servicio / tratamiento</th>
                                                <th className="text-center">Cantidad</th>
                                                <th className="text-center">Valor total</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {!buscado && (
                                                <tr>
                                                    <td colSpan={porPaciente ? 6 : 4} className="text-center text-muted py-4">
                                                        Seleccione filtros y pulse Consultar
                                                    </td>
                                                </tr>
                                            )}
                                            {buscado && filas.length === 0 && (
                                                <tr>
                                                    <td colSpan={porPaciente ? 6 : 4} className="text-center text-muted py-4">
                                                        Sin resultados para los filtros seleccionados
                                                    </td>
                                                </tr>
                                            )}
                                            {filas.map((fila, idx) => (
                                                <tr key={`${fila.paciente_id || 's'}-${fila.id_listadeprecios}-${idx}`}>
                                                    {porPaciente && (
                                                        <>
                                                            <td className="text-center">{fila.numero_documento || '—'}</td>
                                                            <td className="text-center">{fila.paciente_nombre || '—'}</td>
                                                        </>
                                                    )}
                                                    <td className="text-center">{fila.codigo || '—'}</td>
                                                    <td className="text-center">{fila.servicio_nombre || '—'}</td>
                                                    <td className="text-center">{Number(fila.cantidad_total || 0).toLocaleString('es-CO')}</td>
                                                    <td className="text-center">{formatearMoneda(fila.valor_total)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                        {buscado && filas.length > 0 && (
                                            <tfoot>
                                                <tr>
                                                    <td colSpan={porPaciente ? 4 : 2} className="text-left">Totales</td>
                                                    <td className="text-center">{Number(totales.cantidad_total || 0).toLocaleString('es-CO')}</td>
                                                    <td className="text-center">{formatearMoneda(totales.valor_total)}</td>
                                                </tr>
                                            </tfoot>
                                        )}
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            </div>
            <Footer showToast={false} delay={0} />
        </div>
    );
}

export default function ReporteTratamientosFacturados() {
    return <ReporteTratamientosFacturadosBase porPaciente={false} />;
}

export function ReporteTratamientosFacturadosPaciente() {
    return <ReporteTratamientosFacturadosBase porPaciente={true} />;
}
